import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Users as UsersIcon, Plus, Edit, Trash2, Mail, Phone, Shield, Camera, Upload, Download, X, Building2 } from 'lucide-react';
import BuildingAssignmentModal from '../components/team/BuildingAssignmentModal';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Equipe = () => {
  const { token, user } = useAuth();
  const [team, setTeam] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingMember, setEditingMember] = useState(null);
  const [formData, setFormData] = useState({ nom: '', prenom: '', email: '', telephone: '', role: 'assistant', permissions: [], photo_url: '', building_ids: [] });
  const [photoPreview, setPhotoPreview] = useState(null);
  const [uploadingPhoto, setUploadingPhoto] = useState(null); // member id being uploaded
  const [importing, setImporting] = useState(false);
  const [showBuildingModal, setShowBuildingModal] = useState(false);
  const [selectedMemberForBuildings, setSelectedMemberForBuildings] = useState(null);
  const [availableBuildings, setAvailableBuildings] = useState([]);
  const [loadingBuildings, setLoadingBuildings] = useState(false);
  const fileInputRef = useRef(null);
  const csvInputRef = useRef(null);
  const cardFileInputRefs = useRef({});

  // Check if current user can assign buildings - normalize role to lowercase for comparison
  const userRole = (user?.role || '').toLowerCase();
  const canAssignBuildings = ['admin', 'superadmin', 'directeur_cabinet', 'gestionnaire'].includes(userRole);

  const availablePermissions = [
    { key: 'incidents', label: 'Gérer les incidents' },
    { key: 'assemblees', label: 'Gérer les AG' },
    { key: 'documents', label: 'Gérer les documents' },
    { key: 'coproprietaires', label: 'Gérer les coproprietaires' },
    { key: 'messagerie', label: 'Accès messagerie' }
  ];

  useEffect(() => { fetchTeam(); }, [token]);

  // Fetch available buildings when modal opens for creation
  useEffect(() => {
    if (showModal && !editingMember && canAssignBuildings) {
      fetchAvailableBuildings();
    }
  }, [showModal, editingMember]);

  const fetchAvailableBuildings = async () => {
    try {
      setLoadingBuildings(true);
      const response = await axios.get(`${API}/equipe/available-buildings`, { 
        headers: { Authorization: `Bearer ${token}` } 
      });
      setAvailableBuildings(response.data || []);
    } catch (error) {
      console.error('Error fetching buildings:', error);
      setAvailableBuildings([]);
    } finally {
      setLoadingBuildings(false);
    }
  };

  const fetchTeam = async () => {
    try {
      const response = await axios.get(`${API}/equipe`, { headers: { Authorization: `Bearer ${token}` } });
      setTeam(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error('Error fetching team:', error);
      setTeam([]); // Ensure team is always an array
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const dataToSend = { ...formData };
      if (photoPreview) {
        dataToSend.photo_url = photoPreview;
      }
      
      if (editingMember) {
        await axios.put(`${API}/equipe/${editingMember.id}`, dataToSend, { headers: { Authorization: `Bearer ${token}` } });
      } else {
        await axios.post(`${API}/equipe`, dataToSend, { headers: { Authorization: `Bearer ${token}` } });
      }
      setShowModal(false);
      setEditingMember(null);
      setFormData({ nom: '', prenom: '', email: '', telephone: '', role: 'assistant', permissions: [], photo_url: '', building_ids: [] });
      setPhotoPreview(null);
      fetchTeam();
    } catch (error) {
      console.error('Error saving team member:', error);
    }
  };

  const handleEdit = (member) => {
    setEditingMember(member);
    // Handle permissions - ensure it's always an array
    let perms = member.permissions || [];
    if (!Array.isArray(perms)) {
      // Convert object permissions to array format if needed
      perms = [];
    }
    setFormData({
      nom: member.nom || '', prenom: member.prenom || '', email: member.email || '',
      telephone: member.telephone || '', role: member.role || 'assistant', permissions: perms,
      photo_url: member.photo_url || '', building_ids: member.building_ids || []
    });
    setPhotoPreview(member.photo_url || null);
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm('Êtes-vous sûr de vouloir supprimer ce membre ?')) {
      try {
        await axios.delete(`${API}/equipe/${id}`, { headers: { Authorization: `Bearer ${token}` } });
        fetchTeam();
      } catch (error) {
        console.error('Error deleting member:', error);
      }
    }
  };

  const togglePermission = (key) => {
    setFormData(prev => ({
      ...prev,
      permissions: prev.permissions.includes(key)
        ? prev.permissions.filter(p => p !== key)
        : [...prev.permissions, key]
    }));
  };

  const toggleBuilding = (buildingId) => {
    setFormData(prev => ({
      ...prev,
      building_ids: prev.building_ids.includes(buildingId)
        ? prev.building_ids.filter(id => id !== buildingId)
        : [...prev.building_ids, buildingId]
    }));
  };

  const getRoleColor = (role) => {
    const colors = { admin: 'bg-purple-100 text-purple-700', gestionnaire: 'bg-blue-100 text-blue-700', assistant: 'bg-green-100 text-green-700' };
    return colors[role] || 'bg-gray-100 text-gray-700';
  };

  // Handle photo selection for modal
  const handlePhotoChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        alert('La taille de l\'image ne doit pas dépasser 5 Mo');
        return;
      }
      const reader = new FileReader();
      reader.onloadend = () => {
        setPhotoPreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  // Handle direct photo upload on card
  const handleCardPhotoUpload = async (memberId, e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    if (file.size > 5 * 1024 * 1024) {
      alert('La taille de l\'image ne doit pas dépasser 5 Mo');
      return;
    }
    
    setUploadingPhoto(memberId);
    
    const reader = new FileReader();
    reader.onloadend = async () => {
      try {
        await axios.post(`${API}/equipe/${memberId}/photo`, 
          { photo: reader.result }, 
          { headers: { Authorization: `Bearer ${token}` } }
        );
        fetchTeam();
      } catch (error) {
        console.error('Error uploading photo:', error);
        alert('Erreur lors de l\'upload de la photo');
      } finally {
        setUploadingPhoto(null);
      }
    };
    reader.readAsDataURL(file);
  };

  const removePhoto = () => {
    setPhotoPreview(null);
    setFormData(prev => ({ ...prev, photo_url: '' }));
  };

  // Export team to CSV
  const handleExport = () => {
    const headers = ['Prénom', 'Nom', 'Email', 'Téléphone', 'Rôle', 'Permissions'];
    const csvData = team.map(member => [
      member.prenom || '',
      member.nom || '',
      member.email || '',
      member.telephone || '',
      member.role || '',
      (member.permissions || []).join(';')
    ]);
    
    const csvContent = [
      headers.join(','),
      ...csvData.map(row => row.map(cell => `"${cell}"`).join(','))
    ].join('\n');
    
    const blob = new Blob(['\ufeff' + csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `equipe_${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
  };

  // Import team from CSV
  const handleImport = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    setImporting(true);
    const reader = new FileReader();
    
    reader.onload = async (event) => {
      try {
        const text = event.target.result;
        const lines = text.split('\n').filter(line => line.trim());
        
        // Skip header row
        const dataLines = lines.slice(1);
        let imported = 0;
        let errors = 0;
        
        for (const line of dataLines) {
          // Parse CSV line (handle quoted fields)
          const fields = line.match(/(".*?"|[^,]+)(?=\s*,|\s*$)/g) || [];
          const cleanFields = fields.map(f => f.replace(/^"|"$/g, '').trim());
          
          if (cleanFields.length >= 4) {
            const [prenom, nom, email, telephone, role, permissions] = cleanFields;
            
            if (email) {
              try {
                await axios.post(`${API}/equipe`, {
                  prenom,
                  nom,
                  email,
                  telephone: telephone || '',
                  role: role || 'assistant',
                  permissions: permissions ? permissions.split(';').filter(p => p) : []
                }, { headers: { Authorization: `Bearer ${token}` } });
                imported++;
              } catch (err) {
                errors++;
                console.error(`Error importing ${email}:`, err);
              }
            }
          }
        }
        
        alert(`Import terminé: ${imported} membre(s) importé(s), ${errors} erreur(s)`);
        fetchTeam();
      } catch (error) {
        console.error('Error importing CSV:', error);
        alert('Erreur lors de l\'import du fichier CSV');
      } finally {
        setImporting(false);
        if (csvInputRef.current) csvInputRef.current.value = '';
      }
    };
    
    reader.readAsText(file);
  };

  // Download CSV template
  const downloadTemplate = () => {
    const headers = ['Prénom', 'Nom', 'Email', 'Téléphone', 'Rôle', 'Permissions'];
    const example = ['Jean', 'Dupont', 'jean.dupont@email.com', '0612345678', 'assistant', 'incidents;messagerie'];
    const csvContent = [headers.join(','), example.join(',')].join('\n');
    
    const blob = new Blob(['\ufeff' + csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = 'modele_equipe.csv';
    link.click();
  };

  if (loading) {
    return <div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#00D3A8]"></div></div>;
  }

  return (
    <div className="p-6 max-w-7xl mx-auto" data-testid="equipe-page">
      {/* Header - Style unifié */}
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4 mb-6">
        <div className="flex items-center gap-3">
          <div className="p-3 bg-[#00D3A8]/10 rounded-xl">
            <UsersIcon className="h-8 w-8 text-[#00D3A8]" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Mon Équipe</h1>
            <p className="text-gray-500">Gérez les membres de votre équipe et leurs permissions</p>
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <button
            onClick={() => csvInputRef.current?.click()}
            disabled={importing}
            className="px-3 py-2 border border-gray-200 rounded-lg text-sm text-gray-600 hover:bg-gray-50 flex items-center gap-1.5 transition-colors"
            data-testid="import-team-btn"
          >
            <Upload size={16} />
            <span>{importing ? 'Import...' : 'Importer'}</span>
          </button>
          <button
            onClick={handleExport}
            className="px-3 py-2 border border-gray-200 rounded-lg text-sm text-gray-600 hover:bg-gray-50 flex items-center gap-1.5 transition-colors"
            data-testid="export-team-btn"
          >
            <Download size={16} />
            <span>Exporter</span>
          </button>
          <button 
            onClick={() => { setEditingMember(null); setFormData({ nom: '', prenom: '', email: '', telephone: '', role: 'assistant', permissions: [], photo_url: '', building_ids: [] }); setPhotoPreview(null); setShowModal(true); }}
            className="flex items-center gap-2 px-5 py-2.5 bg-[#00D3A8] text-white rounded-full font-medium shadow-md hover:bg-[#00B892] transition-all"
            data-testid="add-member-btn"
          >
            <Plus size={20} /><span>Ajouter un membre</span>
          </button>
        </div>
      </div>

      {/* Hidden CSV input */}
      <input
        type="file"
        ref={csvInputRef}
        onChange={handleImport}
        accept=".csv"
        className="hidden"
      />

      {/* Import info banner */}
      {team.length === 0 && (
        <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
          <div className="flex items-start gap-3">
            <Upload className="text-blue-500 flex-shrink-0 mt-0.5" size={20} />
            <div>
              <p className="text-sm font-medium text-blue-800">Importez votre équipe</p>
              <p className="text-xs text-blue-600">Téléchargez le modèle CSV et importez plusieurs membres en une fois</p>
            </div>
          </div>
          <button
            onClick={downloadTemplate}
            className="text-sm text-blue-600 hover:text-blue-800 underline whitespace-nowrap"
          >
            Télécharger le modèle
          </button>
        </div>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
        {team.map((member) => (
          <div key={member.id} className="bg-white rounded-xl shadow-[0_2px_8px_rgba(0,0,0,0.04)] border border-slate-100 p-4 md:p-6 hover:shadow-[0_4px_12px_rgba(0,0,0,0.06)] transition-shadow" data-testid={`member-card-${member.id}`}>
            <div className="flex items-start justify-between mb-4">
              {/* Photo avatar with upload button */}
              <div className="relative group">
                {member.photo_url ? (
                  <img 
                    src={member.photo_url} 
                    alt={`${member.prenom} ${member.nom}`}
                    className="w-14 h-14 md:w-16 md:h-16 rounded-full object-cover border-2 border-[#00D3A8]/20"
                    data-testid={`member-photo-${member.id}`}
                  />
                ) : (
                  <div className="w-16 h-16 bg-gradient-to-br from-[#00D3A8]/20 to-[#00D3A8]/10 rounded-full flex items-center justify-center">
                    <span className="text-[#00D3A8] font-bold text-lg">{member.prenom?.[0]}{member.nom?.[0]}</span>
                  </div>
                )}
                {/* Upload overlay on hover */}
                <div 
                  className="absolute inset-0 bg-black/50 rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity cursor-pointer"
                  onClick={() => cardFileInputRefs.current[member.id]?.click()}
                  data-testid={`upload-photo-btn-${member.id}`}
                >
                  {uploadingPhoto === member.id ? (
                    <div className="animate-spin rounded-full h-5 w-5 border-2 border-white border-t-transparent"></div>
                  ) : (
                    <Camera size={20} className="text-white" />
                  )}
                </div>
                <input 
                  type="file"
                  ref={el => cardFileInputRefs.current[member.id] = el}
                  onChange={(e) => handleCardPhotoUpload(member.id, e)}
                  accept="image/*"
                  className="hidden"
                  data-testid={`photo-input-${member.id}`}
                />
              </div>
              <div className="flex space-x-2">
                {canAssignBuildings && (member.role || '').toLowerCase() !== 'directeur_cabinet' && (
                  <button 
                    onClick={() => { setSelectedMemberForBuildings(member); setShowBuildingModal(true); }} 
                    className="text-gray-400 hover:text-[#00D3A8] transition-colors p-1" 
                    title="Assigner des immeubles"
                    data-testid={`assign-buildings-${member.id}`}
                  >
                    <Building2 size={18} />
                  </button>
                )}
                <button onClick={() => handleEdit(member)} className="text-gray-400 hover:text-[#00D3A8] transition-colors p-1" data-testid={`edit-member-${member.id}`}><Edit size={18} /></button>
                <button onClick={() => handleDelete(member.id)} className="text-gray-400 hover:text-red-500 transition-colors p-1" data-testid={`delete-member-${member.id}`}><Trash2 size={18} /></button>
              </div>
            </div>
            <h3 className="font-semibold text-gray-800 font-[Manrope]">{member.prenom} {member.nom}</h3>
            <span className={`inline-block mt-1 text-xs px-2.5 py-0.5 rounded-full font-medium ${getRoleColor(member.role)}`}>{member.role}</span>
            <div className="mt-3 text-sm text-gray-500 space-y-1">
              <p className="flex items-center"><Mail size={14} className="mr-2 text-gray-400" />{member.email}</p>
              {member.telephone && <p className="flex items-center"><Phone size={14} className="mr-2 text-gray-400" />{member.telephone}</p>}
            </div>
            
            {/* Building assignments */}
            {member.building_ids?.length > 0 && (
              <div className="mt-4 pt-4 border-t border-gray-100">
                <p className="text-xs text-gray-400 mb-2 flex items-center"><Building2 size={12} className="mr-1" />Immeubles assignés</p>
                <p className="text-sm text-[#00D3A8] font-medium">{member.building_ids.length} immeuble(s)</p>
              </div>
            )}
            
            {Array.isArray(member.permissions) && member.permissions.length > 0 && (
              <div className="mt-4 pt-4 border-t border-gray-100">
                <p className="text-xs text-gray-400 mb-2 flex items-center"><Shield size={12} className="mr-1" />Permissions</p>
                <div className="flex flex-wrap gap-1">
                  {member.permissions.map(p => (
                    <span key={p} className="text-xs bg-gray-100 text-gray-600 px-2 py-0.5 rounded">{p}</span>
                  ))}
                </div>
              </div>
            )}
          </div>
        ))}
      </div>

      {team.length === 0 && (
        <div className="text-center py-12 text-gray-500">
          <Users size={48} className="mx-auto mb-4 text-gray-300" />
          <p>Aucun membre dans l'équipe</p>
        </div>
      )}

      {/* Modal for create/edit member */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 backdrop-blur-sm">
          <div className="bg-white rounded-xl p-6 max-w-md w-full mx-4 max-h-[90vh] overflow-y-auto shadow-2xl" data-testid="member-modal">
            <h2 className="text-xl font-semibold mb-4 font-[Manrope] text-gray-800">{editingMember ? 'Modifier' : 'Ajouter'} un membre</h2>
            <form onSubmit={handleSubmit} className="space-y-4">
              
              {/* Photo upload section in modal */}
              <div className="flex flex-col items-center mb-4">
                <div className="relative group">
                  {photoPreview ? (
                    <div className="relative">
                      <img 
                        src={photoPreview} 
                        alt="Preview"
                        className="w-24 h-24 rounded-full object-cover border-2 border-[#00D3A8]"
                      />
                      <button 
                        type="button"
                        onClick={removePhoto}
                        className="absolute -top-1 -right-1 bg-red-500 text-white rounded-full p-1 hover:bg-red-600 transition-colors"
                        data-testid="remove-photo-btn"
                      >
                        <X size={14} />
                      </button>
                    </div>
                  ) : (
                    <div 
                      className="w-24 h-24 bg-gradient-to-br from-[#00D3A8]/20 to-[#00D3A8]/10 rounded-full flex items-center justify-center cursor-pointer hover:from-[#00D3A8]/30 hover:to-[#00D3A8]/20 transition-all border-2 border-dashed border-[#00D3A8]/30"
                      onClick={() => fileInputRef.current?.click()}
                    >
                      <Upload size={28} className="text-[#00D3A8]" />
                    </div>
                  )}
                </div>
                <input 
                  type="file"
                  ref={fileInputRef}
                  onChange={handlePhotoChange}
                  accept="image/*"
                  className="hidden"
                  data-testid="modal-photo-input"
                />
                <button 
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  className="mt-2 text-sm text-[#00D3A8] hover:text-[#00C7A1] font-medium transition-colors"
                  data-testid="upload-photo-modal-btn"
                >
                  {photoPreview ? 'Changer la photo' : 'Ajouter une photo'}
                </button>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Prénom</label>
                  <input type="text" value={formData.prenom} onChange={(e) => setFormData({...formData, prenom: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#00D3A8]/20 focus:border-[#00D3A8] outline-none transition-all" required data-testid="input-prenom" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Nom</label>
                  <input type="text" value={formData.nom} onChange={(e) => setFormData({...formData, nom: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#00D3A8]/20 focus:border-[#00D3A8] outline-none transition-all" required data-testid="input-nom" />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                <input type="email" value={formData.email} onChange={(e) => setFormData({...formData, email: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#00D3A8]/20 focus:border-[#00D3A8] outline-none transition-all" required data-testid="input-email" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Téléphone</label>
                <input type="tel" value={formData.telephone} onChange={(e) => setFormData({...formData, telephone: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#00D3A8]/20 focus:border-[#00D3A8] outline-none transition-all" data-testid="input-telephone" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Rôle</label>
                <select value={formData.role} onChange={(e) => setFormData({...formData, role: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#00D3A8]/20 focus:border-[#00D3A8] outline-none transition-all" data-testid="select-role">
                  <option value="assistant">Assistant</option>
                  <option value="gestionnaire">Gestionnaire</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Permissions</label>
                <div className="space-y-2">
                  {availablePermissions.map(perm => (
                    <label key={perm.key} className="flex items-center space-x-2 cursor-pointer group">
                      <input type="checkbox" checked={formData.permissions.includes(perm.key)} onChange={() => togglePermission(perm.key)}
                        className="rounded text-[#00D3A8] focus:ring-[#00D3A8] border-gray-300" data-testid={`permission-${perm.key}`} />
                      <span className="text-sm text-gray-600 group-hover:text-gray-800 transition-colors">{perm.label}</span>
                    </label>
                  ))}
                </div>
              </div>
              
              {/* Building assignment section - only for creation and if user can assign */}
              {!editingMember && canAssignBuildings && (
                <div className="pt-2">
                  <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center gap-2">
                    <Building2 size={16} className="text-[#00D3A8]" />
                    Assigner des immeubles
                  </label>
                  {loadingBuildings ? (
                    <div className="flex items-center justify-center py-4">
                      <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-[#00D3A8]"></div>
                    </div>
                  ) : availableBuildings.length === 0 ? (
                    <p className="text-sm text-gray-500 py-2">Aucun immeuble disponible</p>
                  ) : (
                    <div className="max-h-40 overflow-y-auto border border-gray-200 rounded-lg divide-y divide-gray-100">
                      {availableBuildings.map((building) => (
                        <label 
                          key={building.id} 
                          className={`flex items-center gap-3 p-3 cursor-pointer hover:bg-gray-50 transition-all ${
                            formData.building_ids.includes(building.id) ? 'bg-[#00D3A8]/5' : ''
                          }`}
                        >
                          <input 
                            type="checkbox" 
                            checked={formData.building_ids.includes(building.id)} 
                            onChange={() => toggleBuilding(building.id)}
                            className="rounded text-[#00D3A8] focus:ring-[#00D3A8] border-gray-300"
                            data-testid={`building-checkbox-${building.id}`}
                          />
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium text-gray-800 truncate">{building.nom}</p>
                            <p className="text-xs text-gray-500 truncate">{building.adresse} {building.ville}</p>
                          </div>
                        </label>
                      ))}
                    </div>
                  )}
                  {formData.building_ids.length > 0 && (
                    <p className="text-xs text-[#00D3A8] mt-2 font-medium">
                      {formData.building_ids.length} immeuble(s) sélectionné(s)
                    </p>
                  )}
                </div>
              )}

              <div className="flex justify-end space-x-3 pt-4 border-t border-gray-100">
                <button type="button" onClick={() => { setShowModal(false); setPhotoPreview(null); }} className="px-5 py-2 border border-gray-200 rounded-full hover:bg-gray-50 transition-all font-medium" data-testid="cancel-btn">Annuler</button>
                <button type="submit" className="px-5 py-2 bg-gradient-to-r from-[#00D3A8] to-[#00C7A1] text-white rounded-full hover:opacity-90 transition-all font-medium shadow-md" data-testid="save-btn">Enregistrer</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Building Assignment Modal */}
      {showBuildingModal && selectedMemberForBuildings && (
        <BuildingAssignmentModal
          member={selectedMemberForBuildings}
          token={token}
          onClose={() => { setShowBuildingModal(false); setSelectedMemberForBuildings(null); }}
          onSave={() => fetchTeam()}
        />
      )}
    </div>
  );
};

export default Equipe;
